import {
	type Maybe,
	parseThirdPartyARI,
	type SearchResult,
	type ThirdPartyRestrictionLevel,
} from '@atlassian/search-client';

import { is3pProductKey } from '../constants/products';
import {
	type ConnectorSource,
	FEDERATED_CONNECTOR_SOURCE,
	FULL_CONNECTOR_SOURCE,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
} from '../constants/schemas/3p-config';

import { isProductSmartlink } from './oauth-container';

export interface SearchResultGraphDocument extends SearchResult {
	excerpt: string;
	permissionLevel: Maybe<ThirdPartyRestrictionLevel>;
	subtype: string;
	integrationId: string;
	lastModifiedDate: string;
	initialContributors: {
		name: string;
	}[];
}

const GOOGLE_INTEGRATION_ARI = 'ari:cloud:platform::integration/google';
export const isGoogleDocument = (partial: SearchResult) =>
	partial.__typename === 'SearchResultGoogleDocument' ||
	(isGraphDocument(partial) &&
		partial.integrationId === GOOGLE_INTEGRATION_ARI &&
		partial.subtype === 'document');

export const isGraphDocument = (partial: SearchResult): partial is SearchResultGraphDocument =>
	partial.__typename === 'SearchResultGraphDocument';

export const getProductFromThirdPartyId = ({ thirdPartyId }: { thirdPartyId?: string }) => {
	try {
		if (thirdPartyId) {
			const { resourceOwner } = parseThirdPartyARI(thirdPartyId);
			const resourceType = resourceOwner.split('.').at(1);
			const productKey = resourceType === 'google-drive' ? 'drive' : resourceType || '';
			if (is3pProductKey(productKey)) {
				return productKey;
			}
		}
		return undefined;
	} catch (e) {
		// If parsing fails, we default to undefined
		return undefined;
	}
};

const SMART_LINK_ENTITY_REGEX = /^ari:.*::remote-link/;

export const isAriSmartLink = (resultAri: string) => SMART_LINK_ENTITY_REGEX.test(resultAri);

export const isResultSmartLink = ({
	resultAri,
	productKey,
	thirdPartyConfigs,
}: {
	resultAri: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	productKey?: string;
}) => isAriSmartLink(resultAri) || isProductSmartlink({ productKey, thirdPartyConfigs });

export const getResultConnectorSource = ({
	resultNodeId,
	productKey,
	thirdPartyConfigs,
}: {
	resultNodeId: string;
	productKey?: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}): ConnectorSource | undefined => {
	if (
		isResultSmartLink({
			resultAri: resultNodeId,
			productKey,
			thirdPartyConfigs,
		})
	) {
		return SMARTLINKS_CONNECTOR_SOURCE;
	}

	const config = productKey && is3pProductKey(productKey) && thirdPartyConfigs?.[productKey];
	if (config) {
		return config.isFederated ? FEDERATED_CONNECTOR_SOURCE : FULL_CONNECTOR_SOURCE;
	}
	return undefined;
};
